import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminService } from '../../services';
import { useUser } from '../../context/UserContext';
import {
  AdminModal, AdminPageHeader, AreaBar, Card, DataTable, DetailCard, DropChip, Field, FilterRow, SearchField, SegTrack,
  Avatar, Btn, Pill, Thumb, money, shortDate, Loading, textareaClass, useAdminShell,
} from '../../components/admin/shell';

const STATUSES = ['draft', 'selling', 'funding', 'processing_greenlight', 'greenlit', 'ended', 'dissolved', 'canceled'];
const STATUS_OPTIONS = [{ value: '', label: 'ALL' }, ...STATUSES.map((s) => ({ value: s, label: s.replace('_', ' ').toUpperCase() }))];
const statusWord = (s) => String(s || '').replace('_', ' ').toUpperCase();
const STATUS_TONE = {
  draft: 'neutral', selling: 'info', funding: 'info', processing_greenlight: 'warn',
  greenlit: 'good', ended: 'neutral', dissolved: 'bad', canceled: 'bad',
};
const cancellable = (e) => e && e.status !== 'ended' && e.status !== 'canceled';
const WHEN_OPTIONS = [{ value: 'upcoming', label: 'UPCOMING' }, { value: 'past', label: 'PAST' }, { value: 'all', label: 'ALL' }];

/** "SAT OCT 17 / 8:00 PM" over "IN 3 D" — the DATE column the handoff leads the table with. */
const whenParts = (iso) => {
  if (!iso) return ['NO DATE', ''];
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return ['NO DATE', ''];
  const day = d.toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase();
  const time = d.toLocaleTimeString('en', { hour: 'numeric', minute: '2-digit' }).toUpperCase();
  const mins = Math.round((d.getTime() - Date.now()) / 60000);
  const away = Math.abs(mins) < 60 ? `${Math.abs(mins)} MIN` : Math.abs(mins) < 1440 ? `${Math.round(Math.abs(mins) / 60)} H` : `${Math.round(Math.abs(mins) / 1440)} D`;
  return [`${day} / ${time}`, mins >= 0 ? `IN ${away}` : `${away} AGO`];
};
/** Funding type when the DTO carries it; the list DTO only has `mode`, so that is the fallback. */
const typeWord = (e) => {
  if (e.fundingType === 'party_starter') return 'PARTY STARTER';
  if (e.fundingType === 'guaranteed') return 'GUARANTEED';
  return String(e.mode || '—').toUpperCase();
};

/**
 * EVENTS › EVENTS — spec/sections.md §10. Every event on the platform. CANCEL (row and
 * detail) is `events` · cancel; the detail panel is the nested route /admin/events/:id.
 */
export default function EventsPage() {
  const { can } = useUser();
  const navigate = useNavigate();
  const { area, openPerson, canSeePeople } = useAdminShell();
  const { id: selectedId } = useParams();
  const canCancel = can('events', 'cancel');
  const [status, setStatus] = useState('');
  const [when, setWhen] = useState('upcoming');
  const [sort, setSort] = useState('date');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [detailKey, setDetailKey] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => { setApplied(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const scoped = !!area?.area;
  const load = useCallback(async () => {
    setLoading(true);
    const r = await adminService.getEvents(page, {
      status,
      search: applied,
      when,
      sort: sort === 'distance' && !scoped ? 'date' : sort,
      lat: area?.area?.lat,
      lng: area?.area?.lng,
      radiusMiles: area?.radiusMiles,
    });
    setLoading(false);
    if (r.success) { setRows(r.data.events || []); setTotal(r.data.total || 0); }
  }, [page, status, applied, when, sort, scoped, area?.area?.lat, area?.area?.lng, area?.radiusMiles]);
  useEffect(() => { load(); }, [load]);

  const sortOptions = [
    { value: 'date', label: 'DATE' },
    ...(scoped ? [{ value: 'distance', label: 'DISTANCE' }] : []),
    { value: 'pledged', label: 'PLEDGED' },
  ];
  const upcoming = rows.filter((e) => e.dateTime && new Date(e.dateTime) >= new Date()).length;
  const guests = rows.reduce((n, e) => n + (e.acceptedCount || 0), 0);
  const committed = rows.reduce((n, e) => n + (e.totalPledgedCents || 0), 0);

  return (
    <>
      <AdminPageHeader section="events" meta="Every event on the platform. Scope it to a city, then pull. Cancelling refunds every guest." />
      <AreaBar
        stats={[
          { value: total.toLocaleString(), label: 'EVENTS' },
          { value: upcoming.toLocaleString(), label: 'UPCOMING' },
          { value: guests.toLocaleString(), label: 'GUESTS' },
          { value: money(committed), label: 'COMMITTED' },
        ]}
        onPull={() => setPage(1)}
        note={(a) => (a.dirty
          ? 'Changes not pulled yet.'
          : a.area
            ? `Every event within ${a.radiusMiles} mi of ${a.area.label}.`
            : 'Everywhere. Search a city to narrow it down.')}
      />
      <FilterRow>
        <SegTrack options={WHEN_OPTIONS} value={when} onChange={(v) => { setWhen(v); setPage(1); }} ariaLabel="When" />
        <DropChip label="STATUS" value={status} onChange={(v) => { setStatus(v); setPage(1); }} options={STATUS_OPTIONS} />
        <DropChip label="SORT" value={sort} onChange={(v) => { setSort(v); setPage(1); }} options={sortOptions} />
        <SearchField value={search} onChange={setSearch} placeholder="SEARCH TITLE, VENUE OR CITY" />
      </FilterRow>
      {selectedId && (
        <EventDetailPanel
          key={`${selectedId}:${detailKey}`}
          eventId={selectedId}
          onBack={() => navigate('/admin/events')}
          onCancel={(e) => setCancelTarget(e)}
        />
      )}
      <DataTable
        columns={[
          {
            key: 'dateTime',
            label: 'DATE',
            fr: 1.1,
            card: 'kicker',
            render: (e) => {
              const [top, away] = whenParts(e.dateTime);
              return (
                <span className="flex min-w-0 flex-col gap-[3px]">
                  <span className="truncate font-bold">{top}</span>
                  {away && <span className="truncate text-[10px] tracking-[0.06em] text-[#9CA3AF]">{away}</span>}
                </span>
              );
            },
          },
          {
            key: 'title',
            label: 'EVENT',
            fr: 2,
            card: 'title',
            render: (e) => (
              <span className="flex min-w-0 items-center gap-3">
                <Thumb url={e.imageUrl} size={48} />
                <span className="flex min-w-0 flex-col gap-[3px]">
                  <span className="truncate font-bold">{e.title}</span>
                  <span className="truncate text-[10px] tracking-[0.06em] text-[#9CA3AF]">{[e.venue, e.city, typeWord(e)].filter(Boolean).join(' · ')}</span>
                </span>
              </span>
            ),
          },
          {
            key: 'hostDisplayName',
            label: 'HOST',
            fr: 1.2,
            // The row belongs to the event; the host cell belongs to the host, so it
            // opens their drawer and stops there.
            render: (e) => (
              <span
                className={`flex min-w-0 items-center gap-2 ${canSeePeople && e.hostUserId ? 'cursor-pointer hover:underline' : ''}`}
                onClick={canSeePeople && e.hostUserId ? (ev) => { ev.stopPropagation(); openPerson(e.hostUserId); } : undefined}
              >
                <Avatar url={e.hostAvatarUrl} name={e.hostDisplayName || e.hostEmail} size={28} />
                <span className="truncate">{e.hostDisplayName || e.hostEmail || '—'}</span>
              </span>
            ),
          },
          { key: 'status', label: 'STATUS', fr: 0.8, card: 'badge', render: (e) => <Pill tone={STATUS_TONE[e.status] || 'neutral'}>{statusWord(e.status)}</Pill> },
          { key: 'acceptedCount', label: 'GUESTS', fr: 0.6, right: true, render: (e) => `${e.acceptedCount ?? 0}${e.maxAttendees ? ` / ${e.maxAttendees}` : ''}` },
          { key: 'totalPledgedCents', label: 'PLEDGED / GOAL', fr: 1, right: true, render: (e) => `${money(e.totalPledgedCents)}${e.fundingGoalCents ? ` / ${money(e.fundingGoalCents)}` : ''}` },
          scoped && { key: 'miles', label: 'DIST', fr: 0.5, right: true, render: (e) => (e.miles == null ? '—' : `${e.miles} MI`) },
        ]}
        rows={rows}
        rowImage={() => undefined}
        loading={loading}
        selected={(e) => String(e.id) === String(selectedId)}
        onRowClick={(e) => navigate(`/admin/events/${e.id}`)}
        hasActions={canCancel}
        actions={(e) => (canCancel && cancellable(e) ? [{ label: 'CANCEL', variant: 'danger', onClick: () => setCancelTarget(e) }] : [])}
        total={total}
        page={page}
        pageSize={20}
        onPage={setPage}
      />

      {/* Page-level modal: opened from the table AND the detail panel. */}
      <CancelEventModal
        event={cancelTarget}
        onClose={() => setCancelTarget(null)}
        onCanceled={() => { setCancelTarget(null); load(); setDetailKey((k) => k + 1); }}
      />
    </>
  );
}

function CancelEventModal({ event, onClose, onCanceled }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(''); setError(''); }, [event]);
  const confirm = async () => {
    setBusy(true);
    const r = await adminService.cancelEvent(event.id, reason.trim());
    setBusy(false);
    if (r.success) onCanceled(); else setError(r.error || 'Could not cancel the event.');
  };
  if (!event) return null;
  return (
    <AdminModal
      open
      onClose={onClose}
      title="CANCEL EVENT"
      kicker="Every guest commitment is refunded. The reason is kept on the record and told to the host."
      footer={<><Btn size="sheet" onClick={onClose}>KEEP IT</Btn><Btn size="sheet" variant="red" onClick={confirm} disabled={busy}>{busy ? 'CANCELING…' : 'CANCEL EVENT'}</Btn></>}
    >
      <Field label="REASON (OPTIONAL)" error={error}>
        <textarea
          id="cancel-reason" aria-label="REASON (OPTIONAL)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for cancellation…"
          rows={2}
          className={`resize-none ${textareaClass}`}
        />
      </Field>
    </AdminModal>
  );
}

function EventDetailPanel({ eventId, onBack, onCancel }) {
  const panelRef = useRef(null);
  useEffect(() => { panelRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }); }, [eventId]);
  const { can } = useUser();
  const { openPerson, canSeePeople } = useAdminShell();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const r = await adminService.getEventDetail(eventId);
      if (cancelled) return;
      setData(r.success ? r.data : null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  if (loading) return <div className="rounded-[24px] bg-white p-6"><Loading /></div>;
  if (!data) return <div className="rounded-[24px] bg-white p-6 font-mono text-[11px] text-[#DC2626]">COULD NOT LOAD THIS EVENT.</div>;
  const { eventInfo: e, host, applications = [] } = data;
  const goal = e.fundingGoalCents || 0;
  const pledged = e.acceptedPledgedCents || e.totalPledgedCents || 0;

  const info = [
    ['DATE', e.dateTime ? new Date(e.dateTime).toLocaleString() : '—'],
    ['WHERE', e.locationAddress || '—'],
    ['PRICE', money(e.ticketPriceCents)],
    ['PLEDGED', money(e.totalPledgedCents)],
    ['GUESTS', `${e.applicationCount ?? 0} · ${e.acceptedCount ?? 0} ACCEPTED · ${e.paidCount ?? 0} PAID`],
    ['FLAKE FEE', e.flakeFeePercentage != null ? `${e.flakeFeePercentage}%` : '—'],
    ['MODE', String(e.mode || '—').toUpperCase()],
    ['CODE', e.inviteCode || '—'],
  ].map(([label, value]) => ({ label, value }));

  return (
    <section ref={panelRef} className="flex flex-col gap-[14px]">
      <DetailCard
        statusLabel={statusWord(e.status)}
        statusTone={STATUS_TONE[e.status] || 'neutral'}
        kicker={typeWord(e)}
        title={e.title}
        sub={host ? `RUN BY ${(host.displayName || host.email || '').toUpperCase()}` : null}
        progress={goal > 0 ? { left: `${money(pledged)} PLEDGED`, right: `GOAL ${money(goal)}`, percent: (pledged / goal) * 100 } : null}
        info={host && canSeePeople
          ? [{ label: 'HOST', value: (host.displayName || host.email || '—').toUpperCase(), onClick: () => openPerson(host.userId) }, ...info]
          : info}
        actions={(
          <>
            <Btn variant="white" size="card" onClick={() => window.open(`/events/${e.inviteCode || e.id}`, '_blank', 'noopener')}>OPEN EVENT PAGE ↗</Btn>
            {can('events', 'cancel') && cancellable(e) && <Btn variant="danger" size="card" onClick={() => onCancel(e)}>CANCEL EVENT</Btn>}
            <Btn variant="quiet" size="card" onClick={onBack}>BACK</Btn>
          </>
        )}
      />
      {e.description && <Card title="DESCRIPTION"><p className="font-mono text-[12px] leading-[1.7] text-[#4B5563]">{e.description}</p></Card>}
      <DataTable
        title={`GUEST LIST (${applications.length})`}
        right={canSeePeople ? 'A GUEST OPENS THEIR DRAWER' : null}
        onRowClick={canSeePeople ? (a) => openPerson(a.userId) : undefined}
        rowImage={(a) => a.avatarUrl}
        imageRadius={999}
        columns={[
          { key: 'displayName', label: 'NAME', fr: 1.3, render: (a) => `${a.displayName || '—'}${a.goldenTicketUsed ? ' ★' : ''}` },
          { key: 'email', label: 'EMAIL', fr: 1.6 },
          { key: 'status', label: 'STATUS', fr: 0.9, render: (a) => <Pill tone={a.status === 'paid' ? 'good' : a.status === 'declined' || a.status === 'withdrawn' ? 'bad' : 'neutral'}>{String(a.status || '').toUpperCase()}</Pill> },
          { key: 'partyCreditsCommitted', label: 'COMMITTED', fr: 0.8, right: true, render: (a) => `${money(a.partyCreditsCommitted)}${a.partyCreditsRefunded ? ' (R)' : ''}` },
          { key: 'attendanceStatus', label: 'ATTENDANCE', fr: 0.8, render: (a) => String(a.attendanceStatus || '—').toUpperCase() },
          { key: 'createdAt', label: 'APPLIED', fr: 0.7, render: (a) => shortDate(a.createdAt) },
        ]}
        rows={applications}
        empty="NO APPLICATIONS."
      />
    </section>
  );

}
